import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import FormularioPostagem from '../Formulario/FormularioPostagem';
import { getAuthToken, getUsuarioSistemaId } from '../utils/auth';
import './MobileFooter.css';
import BASE_URL from '../utils/api';

// API helper (same pattern as ChatWidget)
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const bases = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
  ];
  let lastError = null;
  for (const url of bases) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const config = { method, headers };
      if (data && (method === 'POST' || method === 'PUT')) {
        config.body = JSON.stringify(data);
      }
      const res = await fetch(url, config);
      if (res.ok) {
        const ct = res.headers.get('Content-Type') || '';
        const text = await res.text();
        if (ct.includes('application/json') && text) {
          try { return JSON.parse(text); } catch { return text; }
        }
        return text || true;
      } else {
        lastError = new Error(`${res.status}`);
      }
    } catch (e) { lastError = e; }
  }
  throw lastError || new Error('API error');
};

const apiUploadMultipart = async (endpoint, formData) => {
  const bases = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
  ];
  let lastError = null;
  for (const url of bases) {
    try {
      const headers = {};
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url, { method: 'POST', headers, body: formData });
      if (res.ok) {
        const ct = res.headers.get('Content-Type') || '';
        const text = await res.text();
        if (ct.includes('application/json') && text) {
          try { return JSON.parse(text); } catch { return text; }
        }
        return text || true;
      } else {
        lastError = new Error(`${res.status}`);
      }
    } catch (e) { lastError = e; }
  }
  throw lastError || new Error('Upload error');
};

export default function MobileFooter({ onOpenAjuda, onOpenChat }) {
  const location = useLocation();
  const isHomePage = location.pathname === '/sidebar/Home' || location.pathname === '/sidebar/Home/';
  const [showPostPopup, setShowPostPopup] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  // Listen for unread count updates from ChatWidget
  useEffect(() => {
    const handler = (e) => setChatUnread(e.detail || 0);
    window.addEventListener('chat-unread-update', handler);
    return () => window.removeEventListener('chat-unread-update', handler);
  }, []);

  // Listen for composer short-input click on mobile (opens popup instead of inline expand)
  useEffect(() => {
    const handler = () => {
      if (isHomePage) setShowPostPopup(true);
    };
    window.addEventListener('open-mobile-post-popup', handler);
    return () => window.removeEventListener('open-mobile-post-popup', handler);
  }, [isHomePage]);

  const handlePlusClick = () => {
    if (isHomePage) {
      setShowPostPopup(true);
    } else {
      // Dispatch global event — each page's MobileAddListener picks it up
      window.dispatchEvent(new Event('open-mobile-add-popup'));
    }
  };

  return (
    <>
      <footer className="mobile-footer">
        <div className="mobile-footer-inner">
          {/* Ajuda button - left */}
          <button className="mobile-footer-btn mobile-footer-ajuda" onClick={onOpenAjuda} title="Ajuda">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 1C7.03 1 3 5.03 3 10v5c0 1.66 1.34 3 3 3h1v-7H5.5v-1c0-3.59 2.91-6.5 6.5-6.5s6.5 2.91 6.5 6.5v1H17v7h1c1.66 0 3-1.34 3-3v-5c0-4.97-4.03-9-9-9z" fill="white"/>
              <path d="M5 12v5h2v-5H5zM17 12v5h2v-5h-2z" fill="white"/>
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2z" fill="white"/>
              <path d="M14 20h-4v-1h4v1z" fill="white"/>
            </svg>
          </button>

          {/* Center + button */}
          <button
            className="mobile-footer-plus active"
            onClick={handlePlusClick}
            title="Adicionar"
          >
            <span className="mobile-footer-plus-icon">+</span>
          </button>

          {/* Chat button - right */}
          <button className="mobile-footer-btn mobile-footer-chat" onClick={onOpenChat} title="Chat">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" fill="white"/>
              <path d="M6 6h12v1.5H6zM6 9h12v1.5H6zM6 12h12v1.5H6z" fill="white"/>
            </svg>
            {chatUnread > 0 && (
              <span className="mobile-footer-badge">{chatUnread > 99 ? '99+' : chatUnread}</span>
            )}
          </button>
        </div>
      </footer>

      {/* Post creation popup for mobile */}
      {showPostPopup && (
        <div className="mobile-post-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowPostPopup(false); }}>
          <div className="mobile-post-popup">
            <div className="mobile-post-popup-header">
              <h3>Criar Publicação</h3>
              <button className="mobile-post-popup-close" onClick={() => setShowPostPopup(false)}>✕</button>
            </div>
            <div className="mobile-post-popup-body">
              <FormularioPostagem
                apiCall={apiCall}
                apiUploadMultipart={apiUploadMultipart}
                autoExpand={true}
                hideCancelButton={true}
                onPosted={() => {
                  setShowPostPopup(false);
                  // Reload the page to show new post
                  window.location.reload();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
